import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChromeConfig } from '@chrome/types';
import { getAccountSideNavigationItems } from '@chrome/configuration/sideNavigation';
import {
  ChromeKeys,
  HeaderTranslationKeys,
  MenuItemIds,
  PageLayoutType,
  ButtonType,
  ButtonState,
  PageScope,
} from '@chrome/enums';
import { useGetConfigurationQuery } from '@services/configuration';
import useIntl from '@i18n/useIntl';

const useChromeConfiguration = (
  key: ChromeKeys,
): Partial<ChromeConfig> | undefined => {
  const { intl } = useIntl();
  const {
    data: { entityId = '', entityName = '', globalAccountId = '' } = {},
  } = useGetConfigurationQuery();
  const navigate = useNavigate();

  const configuration = useMemo(() => {
    return new Map([
      [
        ChromeKeys.AccountSettings,
        {
          header: {
            pageName: intl.getMessage(
              HeaderTranslationKeys.AccessAndSettingsPageName,
            ),
            name: '',
            value: globalAccountId || '',
            key: intl.getMessage(HeaderTranslationKeys.AccessAndSettingsKey),
          },
          sideNavigation: {
            items: getAccountSideNavigationItems({
              intl,
              entityId,
              globalAccountId,
              navigate,
            }),
            selected: MenuItemIds.AccountDetails,
          },
          layout: { pageLayout: PageLayoutType.Standard },
          enableMarketplaceSwitcher: false,
        },
      ],
      [
        ChromeKeys.AccountCombination,
        {
          /*
            The combine and upgrade lane acts across two accounts and produces a
            third, so it is not the chrome's default single-account page: the
            switcher reads "All accounts" and the marketplace switcher is hidden,
            since a marketplace is only meaningful for one selected account.
            Account identifiers come from the URL rather than the session's
            entity, so nothing on these pages depends on one being selected.
          */
          pageScope: PageScope.AllAccounts,
          enableMarketplaceSwitcher: false,
        },
      ],
      [
        ChromeKeys.AccountRegistration,
        {
          layout: { pageLayout: PageLayoutType.Light },
          enableMarketplaceSwitcher: false,
        },
      ],
      [
        ChromeKeys.RetailerAccountRegistration,
        {
          layout: { pageLayout: PageLayoutType.Light },
          enableMarketplaceSwitcher: false,
        },
      ],
      [
        ChromeKeys.AdspAccountRegistration,
        {
          layout: { pageLayout: PageLayoutType.Light },
          //Todo set the page name translation when get it from product
          pageName: 'Create an Amazon DSP advertising account',
        },
      ],
      [
        ChromeKeys.IneligibilityRegistration,
        {
          //Todo set the page name translation when get it from product
          pageName: 'Registration ineligibility',
          layout: { pageLayout: PageLayoutType.Standard },
        },
      ],
      [
        ChromeKeys.DspActivationRequest,
        {
          header: {
            pageName: intl.getMessage(
              HeaderTranslationKeys.DspActivationRequestPageName,
            ),
            buttons: new Map([
              [
                'adspSubmitActivationRequest',
                {
                  id: 'adspSubmitActivationRequest',
                  label: intl.getMessage(
                    HeaderTranslationKeys.DspActivationRequestSubmit,
                  ),
                  type: ButtonType.Primary,
                  state: ButtonState.Enabled,
                },
              ],
              [
                'adspCancelActivationRequest',
                {
                  id: 'adspCancelActivationRequest',
                  label: intl.getMessage(HeaderTranslationKeys.Cancel),
                  type: ButtonType.Normal,
                  state: ButtonState.Enabled,
                },
              ],
            ]),
          },
          layout: { pageLayout: PageLayoutType.Focus },
          enableMarketplaceSwitcher: false,
        },
      ],
      [
        ChromeKeys.DspActivationActivate,
        {
          header: {
            pageName: intl.getMessage(
              HeaderTranslationKeys.DspActivationActivatePageName,
            ),
            buttons: new Map([
              [
                'adspSubmitActivation',
                {
                  id: 'adspSubmitActivation',
                  label: intl.getMessage(
                    HeaderTranslationKeys.DspActivationActivateSubmit,
                  ),
                  type: ButtonType.Primary,
                  state: ButtonState.Enabled,
                },
              ],
              [
                'adspCancelActivation',
                {
                  id: 'adspCancelActivation',
                  label: intl.getMessage(HeaderTranslationKeys.Cancel),
                  type: ButtonType.Normal,
                  state: ButtonState.Enabled,
                },
              ],
            ]),
          },
          layout: { pageLayout: PageLayoutType.Focus },
          enableMarketplaceSwitcher: false,
        },
      ],
      [
        ChromeKeys.DspCreateManagerAccount,
        {
          header: {
            pageName: intl.getMessage(
              HeaderTranslationKeys.DspCreateManagerAccountPageName,
            ),
            buttons: new Map([
              [
                'adspSubmitCreateManagerAccount',
                {
                  id: 'adspSubmitCreateManagerAccount',
                  label: intl.getMessage(
                    HeaderTranslationKeys.DspCreateManagerAccountSubmit,
                  ),
                  type: ButtonType.Primary,
                  state: ButtonState.Enabled,
                },
              ],
              [
                'adspCancelCreateManagerAccount',
                {
                  id: 'adspCancelCreateManagerAccount',
                  label: intl.getMessage(HeaderTranslationKeys.Cancel),
                  type: ButtonType.Normal,
                  state: ButtonState.Enabled,
                },
              ],
            ]),
          },
          layout: { pageLayout: PageLayoutType.Focus },
          enableMarketplaceSwitcher: false,
        },
      ],
      [
        ChromeKeys.ManagerAccountCreation,
        {
          header: {
            pageName: intl.getMessage(
              HeaderTranslationKeys.ManagerAccountCreationPageName,
            ),
          },
          layout: { pageLayout: PageLayoutType.Focus },
          enableMarketplaceSwitcher: false,
        },
      ],
    ]);
  }, [entityId, entityName, intl, getAccountSideNavigationItems]);

  return configuration.get(key);
};

export default useChromeConfiguration;
